 import { useSyncExternalStore, useEffect, useState } from 'react';
 import { useNavigate, useLocation } from 'react-router-dom';
import { generationManager, type GenerationJob } from '@/lib/generationManager';
import { Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';

/** Floating banner that shows active/completed generation status globally */
const GenerationBanner = () => {
  const navigate = useNavigate();
   const location = useLocation();
   const [initialPath] = useState(location.pathname);

  const jobs = useSyncExternalStore(
    (cb) => generationManager.subscribe(cb),
    () => generationManager.getJobs()
  );

  // Show the most recent non-idle job
  const visibleJob = jobs.length > 0
    ? jobs.reduce<GenerationJob | null>((best, j) => {
        if (!best || j.startedAt > best.startedAt) return j;
        return best;
      }, null)
    : null;

  const isActive = visibleJob?.status === 'generating' || visibleJob?.status === 'transcribing' || visibleJob?.status === 'saving';
  const isMusic = visibleJob?.contentType === 'music';

  // Auto-dismiss banners after a delay: 6s for generating, 3s for done
  useEffect(() => {
    if (!visibleJob) return;

    let delay = 0;
    if (visibleJob.status === 'done') {
      delay = 3000;
    } else if (isActive) {
      delay = 6000;
    }

    if (delay > 0) {
      const t = setTimeout(() => generationManager.clearJob(visibleJob.id), delay);
      return () => clearTimeout(t);
    }
  }, [visibleJob?.id, visibleJob?.status, isActive]);

  const phrases = isMusic 
    ? [
        "Afinando a meditação teológica...",
        "Buscando as conexões bíblicas da letra...",
        "Quase lá! Sintonizando os últimos detalhes.",
        "Preparando sua Sola Note musical..."
      ]
    : [
        "Destilando a mensagem central...",
        "Organizando os pontos da pregação...",
        "Quase lá! Revisando as aplicações...",
        "Preparando sua Sola Note exclusiva..."
      ];

  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    if (!isActive) {
      setPhraseIdx(0);
      return;
    }
    const interval = setInterval(() => {
      setPhraseIdx(prev => (prev + 1) % phrases.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [isActive, phrases.length]);

  // Only show if job exists and is active/done/error
  if (!visibleJob || visibleJob.status === 'idle') return null;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-fade-in">
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border shadow-lg backdrop-blur-sm">
        {isActive && <Loader2 size={18} className="animate-spin text-accent flex-shrink-0" />}
        {visibleJob.status === 'done' && <CheckCircle2 size={18} className="text-accent flex-shrink-0" />}
        {visibleJob.status === 'error' && <AlertCircle size={18} className="text-destructive flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          {isActive && (
            <p className="text-sm font-medium text-foreground truncate">
              {phrases[phraseIdx]}
            </p>
          )}
          {visibleJob.status === 'done' && (
            <button
              onClick={() => {
                if (visibleJob.noteId) navigate(`/note/${visibleJob.noteId}`);
                generationManager.clearJob(visibleJob.id);
              }}
              className="text-sm font-medium text-accent hover:underline truncate block"
            >
              Nota criada — toque para abrir
            </button>
          )}
          {visibleJob.status === 'error' && (
            <p className="text-sm font-medium text-destructive truncate">
              {visibleJob.errorMsg || 'Erro ao gerar'}
            </p>
          )}
        </div>

        {(visibleJob.status === 'done' || visibleJob.status === 'error') && (
          <button
            onClick={() => generationManager.clearJob(visibleJob.id)}
            className="p-1 rounded-md hover:bg-muted text-subtle flex-shrink-0"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default GenerationBanner;
